import React, { useEffect, useState } from "react";
import { Search, User, X, Link2 } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { namaGelar } from "../lib/utils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import StatusBadge from "./StatusBadge";

// Pemilih orang tua: pilih dari Data Jemaat (searchable, filter jenis kelamin) atau input manual.
export const ParentPicker = ({ label, gender, linked, nama, parentId, onChange, selfId, testId }) => {
  const [mode, setMode] = useState(parentId ? "jemaat" : "manual");
  const [open, setOpen] = useState(false);
  const [confirmManual, setConfirmManual] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (parentId) setMode("jemaat"); }, [parentId]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get("/jemaat", { params: { search: q || undefined, jenis_kelamin: gender, page_size: 10 } });
        setItems((r.data.items || []).filter((x) => x.id !== selfId));
      } catch { setItems([]); } finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, open, gender, selfId]);

  const pick = (j) => {
    if (j.jenis_kelamin !== gender) return;
    onChange({ id: j.id, nama: j.nama_lengkap, brief: j });
    setOpen(false); setQ("");
  };

  const switchMode = (m) => {
    if (m === "manual" && parentId) { setConfirmManual(true); return; }
    setMode(m);
    if (m === "jemaat") onChange({ id: null, nama: "", brief: null });
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex gap-2 text-sm">
        {[["jemaat", "Pilih dari Data Jemaat"], ["manual", `${label} belum terdaftar`]].map(([m, l]) => (
          <button key={m} type="button" onClick={() => switchMode(m)} data-testid={`${testId}-mode-${m}`}
            className={`flex-1 min-h-[44px] rounded-xl border px-3 text-left flex items-center gap-2 transition-colors ${mode === m ? "border-[hsl(224,64%,33%)] bg-blue-50 text-[hsl(224,64%,33%)] font-semibold" : "border-slate-200 text-slate-600"}`}>
            <span className={`h-4 w-4 rounded-full border flex items-center justify-center ${mode === m ? "border-[hsl(224,64%,33%)]" : "border-slate-300"}`}>{mode === m && <span className="h-2 w-2 rounded-full bg-[hsl(224,64%,33%)]" />}</span>
            <span className="text-xs sm:text-sm">{l}</span>
          </button>
        ))}
      </div>

      {mode === "jemaat" ? (
        parentId && linked ? (
          <div className="flex items-center gap-3 p-3 rounded-xl border border-emerald-200 bg-emerald-50" data-testid={`${testId}-linked`}>
            <Avatar j={linked} size="h-12 w-12" />
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-900 truncate flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5 text-emerald-600" />{namaGelar(linked)}</div>
              <div className="font-mono text-xs text-slate-500">NIJ: {linked.nij}</div>
            </div>
            <Button type="button" variant="outline" size="sm" className="rounded-xl min-h-[44px]" onClick={() => setOpen(true)} data-testid={`${testId}-change-btn`}>Ganti</Button>
          </div>
        ) : (
          <Button type="button" variant="outline" className="w-full min-h-[44px] rounded-xl justify-start text-slate-600" onClick={() => setOpen(true)} data-testid={`${testId}-search-btn`}>
            <Search className="h-4 w-4 mr-2" /> Cari Data Jemaat ({gender})
          </Button>
        )
      ) : (
        <div>
          <Input value={nama || ""} onChange={(e) => onChange({ id: null, nama: e.target.value, brief: null })} placeholder={`Nama ${label} (ketik manual)`} className="min-h-[44px] rounded-xl" data-testid={`${testId}-manual-input`} />
          <p className="text-xs text-slate-400 mt-1">Belum terdaftar sebagai jemaat — tidak membuat data jemaat baru.</p>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-lg p-0 overflow-hidden" data-testid={`${testId}-dialog`}>
          <DialogHeader className="p-4 pb-2"><DialogTitle>Cari {label} dari Data Jemaat</DialogTitle><DialogDescription>Hanya jemaat berjenis kelamin {gender}. Cari berdasarkan NIJ atau nama.</DialogDescription></DialogHeader>
          <div className="px-4"><div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ketik NIJ atau nama..." className="pl-9 min-h-[44px] rounded-xl" data-testid={`${testId}-search-input`} /></div></div>
          <div className="max-h-[55vh] overflow-y-auto p-4 space-y-2" data-testid={`${testId}-results`}>
            {loading && <p className="text-sm text-slate-400 text-center py-4">Mencari...</p>}
            {!loading && items.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Tidak ada jemaat {gender} yang cocok.</p>}
            {!loading && items.map((j) => (
              <button key={j.id} type="button" onClick={() => pick(j)} data-testid={`${testId}-result-${j.id}`}
                className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-[hsl(224,64%,33%)] hover:bg-blue-50 text-left transition-colors min-h-[60px]">
                <Avatar j={j} size="h-12 w-12" />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 truncate">{namaGelar(j)}</div>
                  <div className="font-mono text-xs text-slate-500">NIJ: {j.nij}{j.nomor_kk ? ` · KK: ${j.nomor_kk}` : ""}</div>
                  <div className="text-xs text-slate-500">{j.jenis_kelamin}</div>
                </div>
                <StatusBadge value={j.status_jemaat} type="jemaat" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmManual} onOpenChange={(o) => !o && setConfirmManual(false)}>
        <DialogContent className="rounded-2xl" data-testid={`${testId}-confirm-manual`}>
          <DialogHeader><DialogTitle>Putuskan relasi {label}?</DialogTitle>
            <DialogDescription>Data {label} saat ini terhubung dengan jemaat <b>{linked ? namaGelar(linked) : nama}</b>. Apakah Anda yakin ingin mengubah menjadi data manual? Relasi akan diputus saat disimpan.</DialogDescription></DialogHeader>
          <DialogFooter className="flex-row gap-2">
            <Button type="button" variant="outline" className="rounded-xl min-h-[44px] flex-1" onClick={() => setConfirmManual(false)} data-testid={`${testId}-confirm-manual-cancel`}>Batal</Button>
            <Button type="button" className="rounded-xl min-h-[44px] flex-1 bg-red-600 hover:bg-red-700" onClick={() => { setConfirmManual(false); setMode("manual"); onChange({ id: null, nama: nama || "", brief: null }); }} data-testid={`${testId}-confirm-manual-ok`}>Ya, Ubah</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Avatar = ({ j, size }) => (
  <div className={`${size} rounded-xl bg-slate-100 overflow-hidden flex items-center justify-center text-slate-400 shrink-0`}>
    {j.foto_path ? <img src={fileUrl(j.foto_path)} alt="" className="h-full w-full object-cover" /> : <User className="h-5 w-5" />}
  </div>
);

export default ParentPicker;
